<script setup lang="ts">
import { ElIcon } from 'element-plus'
import { computed } from 'vue'

import { BoxComponent, FlexComponent, ProfileImageComponent, TypographyComponent } from '..'
import AddIcon from '../../assets/icons/add-plus.svg?component'
import CheckIcon from '../../assets/icons/check.svg?component'
import type { ProcessableImage } from '../../types/common.types'

const props = withDefaults(
  defineProps<{
    username: string
    id: number
    /** Left out to hide the email and its separator — the meta line falls back to the id alone. */
    email?: string
    image?: ProcessableImage
    /** Initials fallback for the avatar when `username` is empty. */
    avatarName?: string
    added?: boolean
    /**
     * Trailing affordance: the Add/Added pill of a multi-add flow, or the plain check every
     * selector uses to mark its selected option. Both are read-only state — the row is rendered
     * inside a dropdown item that owns the click (see `Selector` and `MultiAddSelector`).
     */
    indicator?: 'pill' | 'check'
  }>(),
  {
    added: false,
    indicator: 'pill',
  },
)

const metaText = computed(() => (props.email ? `ID ${props.id} • ${props.email}` : `ID ${props.id}`))

const avatarName = computed(() => props.username || props.avatarName || '')
</script>

<template>
  <BoxComponent :p="16" w="100%">
    <FlexComponent justify="space-between" align="center" :gap="16">
      <FlexComponent align="center" flex="1" :gap="12" class="profile-option-item__content">
        <ProfileImageComponent :image="image" :name="avatarName" :size="32" class="profile-option-item__avatar" />
        <FlexComponent direction="column" :gap="2" class="profile-option-item__text">
          <TypographyComponent variant="title-medium" text-truncate :title="username">
            {{ username }}
          </TypographyComponent>
          <TypographyComponent variant="label-small" text-truncate :title="metaText" class="profile-option-item__meta">
            {{ metaText }}
          </TypographyComponent>
        </FlexComponent>
      </FlexComponent>
      <ElIcon v-if="indicator === 'check' && added" :size="24" class="profile-option-item__indicator">
        <CheckIcon />
      </ElIcon>
      <BoxComponent
        v-else-if="indicator === 'pill'"
        class="profile-option-item__pill profile-option-item__indicator"
        :class="{ 'profile-option-item__pill_added': added }"
      >
        <FlexComponent justify="center" align="center" :gap="4">
          <ElIcon :size="18">
            <CheckIcon v-if="added" />
            <AddIcon v-else />
          </ElIcon>
          <TypographyComponent variant="label-large" :font-weight="600" :color="added ? 'var(--success)' : undefined">
            {{ added ? 'Added' : 'Add' }}
          </TypographyComponent>
        </FlexComponent>
      </BoxComponent>
    </FlexComponent>
  </BoxComponent>
</template>

<style lang="scss" scoped>
.profile-option-item__content,
.profile-option-item__text {
  min-width: 0;
}
.profile-option-item__avatar,
.profile-option-item__indicator {
  flex-shrink: 0;
}
.profile-option-item__meta {
  opacity: 0.48;
}
.profile-option-item__pill {
  height: 32px;
  display: flex;
  align-items: center;
  cursor: pointer;
  color: var(--primary);
  &_added {
    color: var(--success);
  }
  :deep(.el-icon) {
    margin-right: 0;
    path {
      fill: currentColor;
    }
  }
}
</style>
